import { useCallback, useEffect, useState } from 'react'
import {
  chargerConfiguration,
  enregistrerBonusMeteo,
  enregistrerPaliersLivraison,
  enregistrerReglage,
  ErreurMigration0043,
  MESSAGE_MIGRATION_0043,
} from '../../api/dispatch'
import type { Configuration, PalierLivraison } from '../../api/types'
import {
  apercuPrix,
  DISTANCES_APERCU,
  fmtKm,
  libelleMeteo,
  lireNombre,
  trierPaliers,
  validerFraisService,
  validerMinima,
  validerPaliers,
  type FraisServiceASaisir,
  type LigneApercu,
  type MinimaASaisir,
  type PalierASaisir,
} from '../../api/tarifs'
import { fcfa } from '../Format'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'
import { enregistrerPaysDefaut, lirePaysDefautConfig, optionsPays } from '../../api/paysDefaut'
import { CarteMeteo } from '../meteo/CarteMeteo'

/**
 * Config — TRANSPOSITION du gabarit d'origine.
 *
 * ⚠️ Ce portage était en LECTURE SEULE, avec un bandeau renvoyant vers
 * l'ancienne console. C'était honnête, mais ce n'était pas la parité : ces
 * réglages sont les barèmes de la plateforme, et les modifier est une tâche
 * quotidienne du dispatch. Deux tableaux entiers manquaient aussi — les tarifs
 * de livraison par rayon et les bonus météo.
 *
 * La mise en page vient de `.config-grid` (deux cartes côte à côte) et de
 * `.config-field` / `.cf-label` (libellé à gauche, champ à droite), trois
 * classes qui n'étaient posées nulle part.
 */
export function EcranConfig() {
  const [config, setConfig] = useState<Configuration | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  /* Une écriture a été refusée pour « colonne inconnue » : la base de
     production n'a pas encore la migration 0043. Ce n'est pas une panne. */
  const [migrationAbsente, setMigrationAbsente] = useState(false)

  const charger = useCallback(async () => {
    try {
      setConfig(await chargerConfiguration())
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture impossible.')
    }
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  if (erreur && !config) return <p style={{ color: 'var(--danger)', padding: 18 }}>{erreur}</p>
  if (!config) return <p style={{ padding: 18, color: 'var(--n400)' }}>Chargement…</p>

  /**
   * Un champ de réglage. La valeur n'est envoyée qu'à la SORTIE du champ, pas
   * à chaque frappe : enregistrer « 1 » puis « 12 » puis « 125 » en tapant
   * 125 poserait trois barèmes différents, dont deux absurdes.
   */
  const Champ = ({
    libelle,
    cle,
    valeur,
    largeur,
    infobulle,
    desactive,
  }: {
    libelle: string
    cle: string
    valeur: string | number
    largeur?: number
    infobulle?: string
    desactive?: boolean
  }) => (
    <div className="config-field">
      <span className="cf-label">{libelle}</span>
      <input
        defaultValue={String(valeur)}
        title={infobulle}
        disabled={desactive}
        style={largeur ? { width: largeur } : undefined}
        onBlur={(e) => {
          if (e.target.value === String(valeur)) return
          void enregistrerReglage(cle, e.target.value)
            .then(charger)
            .catch((err: unknown) => {
              if (err instanceof ErreurMigration0043) setMigrationAbsente(true)
              else
                setErreur(
                  `Enregistrement refusé : ${err instanceof Error ? err.message : String(err)}`,
                )
              // On REPOSE l'ancienne valeur : laisser la saisie refusée à
              // l'écran ferait croire qu'elle est en vigueur.
              e.target.value = String(valeur)
            })
        }}
      />
    </div>
  )

  return (
    <>
      {erreur && <p style={{ color: 'var(--danger)', fontSize: 13, padding: '0 2px' }}>{erreur}</p>}

      <CarteMeteo
        config={config}
        sans0043={config.meteoActuelle === null || migrationAbsente}
        surMigrationAbsente={() => setMigrationAbsente(true)}
        surErreur={setErreur}
        surSucces={charger}
      />

      <div className="config-grid">
        <div className="card">
          <div className="card-header">Tarification plateforme</div>
          <div className="card-body">
            <Champ
              libelle="Commission initiale d'un nouveau marchand (%)"
              cle="default_commission_pct"
              valeur={config.commissionParDefaut}
              infobulle="Taux contractuel donné à un marchand à sa création (modifiable ensuite dans sa fiche). Chaque nouvel article prend le taux de son marchand ; le prix client = prix du marchand × (1 + taux)."
            />
            {config.commandeMinimum !== null && (
              <Champ
                libelle="Commande minimum (FCFA)"
                cle="commande_minimum"
                valeur={config.commandeMinimum}
                infobulle="Sous-total client en dessous duquel on ne peut pas commander, sauf minimum propre au restaurant (fiche marchand). Entre ce minimum et le seuil petite commande, le supplément petite commande s'applique."
              />
            )}
            {config.reductionPremium !== null && (
              <Champ
                libelle="Réduction Premium sur la livraison (%)"
                cle="premium_reduction_pct"
                valeur={config.reductionPremium}
                largeur={80}
                infobulle="Un abonné paie les frais de livraison × (1 − réduction), arrondis à 50 F. Il ne paie ni supplément petite commande, ni supplément météo (ni bonus météo au livreur sur sa commande), ni majoration de forte demande."
              />
            )}
            {config.minimaLivraison === null ? (
              <Champ libelle="Frais de service (FCFA)" cle="service_fee" valeur={config.fraisService} />
            ) : (
              <div className="config-field">
                <span className="cf-label">Frais de service</span>
                <span style={{ fontSize: 12.5, color: 'var(--n400)' }}>
                  selon la distance — carte « Tarifs livraison », plus bas
                </span>
              </div>
            )}
            <Champ
              libelle="Seuil petite commande (FCFA)"
              cle="small_order_threshold"
              valeur={config.seuilPetiteCommande}
            />
            <Champ
              libelle="Supplement petite commande (FCFA)"
              cle="small_order_fee"
              valeur={config.fraisPetiteCommande}
            />
            <Champ
              libelle="Seuil livraison gratuite (FCFA)"
              cle="free_delivery_threshold"
              valeur={config.seuilLivraisonOfferte}
            />
            <Champ libelle="Pointe midi (hh:mm-hh:mm)" cle="peak_lunch" valeur={config.pointeMidi} largeur={130} />
            <Champ libelle="Pointe soir (hh:mm-hh:mm)" cle="peak_dinner" valeur={config.pointeSoir} largeur={130} />
            <Champ
              libelle="Abonnement Premium (FCFA/mois)"
              cle="premium_monthly"
              valeur={config.premiumMensuel}
            />
            <Champ libelle="1er jour de paie (du mois)" cle="payout_day_1" valeur={config.jourPaie1} largeur={70} />
            <Champ libelle="2e jour de paie (du mois)" cle="payout_day_2" valeur={config.jourPaie2} largeur={70} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">Primes &amp; Bonus</div>
          <div className="card-body">
            <Champ libelle="Bonus pointe (FCFA)" cle="peak_bonus" valeur={config.bonusPointe} />
            <Champ
              libelle="Seuil volume (courses/jour)"
              cle="volume_threshold"
              valeur={config.seuilVolume}
            />
            <Champ libelle="Bonus volume (FCFA)" cle="volume_bonus" valeur={config.bonusVolume} />
            <Champ
              libelle="Seuil fidélité (courses/mois)"
              cle="loyalty_threshold"
              valeur={config.seuilFidelite}
            />
            <Champ
              libelle="Bonus fidélité (FCFA/mois)"
              cle="loyalty_bonus"
              valeur={config.bonusFidelite}
            />
            {/* Les règles calculées par la base (migration 0060), dites ici pour
                que personne ne règle un montant en croyant à une autre règle. */}
            <p style={{ fontSize: 12, color: 'var(--n400)', margin: '4px 0 10px', lineHeight: 1.5 }}>
              Pointe : par course livrée dans une plage (heure de Bamako, fin exclue), montant figé à la
              livraison. Volume : une fois par jour où le seuil est atteint. Fidélité : une fois par mois
              civil où le seuil est atteint, versée sur la paie du 16 à la fin du mois. Un changement ne
              vaut que pour l’avenir : volume et fidélité prennent le réglage en vigueur à la fin du jour
              ou du mois. Seules les courses livrées comptent.
            </p>
            <Champ
              libelle="Note auto si non-reponse (/5)"
              cle="auto_rating"
              valeur={config.noteAuto}
              largeur={80}
            />
          </div>
        </div>

        {/* ⚠️ Ces deux réglages sont les seuls de cet écran qui touchent à la
            COMPTABILITÉ et non aux barèmes. Assujettir l'entreprise change
            rétroactivement tous les états : le chiffre d'affaires passe hors
            taxes et une dette envers l'État apparaît au bilan. */}
        <div className="card">
          <div className="card-header">TVA</div>
          <div className="card-body">
            <div className="config-field">
              <span className="cf-label">Entreprise assujettie</span>
              <ListeDeroulante
                valeur={config.tvaActive ? 'oui' : 'non'}
                options={[
                  { valeur: 'non', libelle: 'Non (impôt synthétique)' },
                  { valeur: 'oui', libelle: 'Oui' },
                ]}
                surChangement={(v) => {
                  void enregistrerReglage('vat_enabled', v)
                    .then(charger)
                    .catch((err: unknown) =>
                      setErreur(
                        `Enregistrement refusé : ${err instanceof Error ? err.message : String(err)}`,
                      ),
                    )
                }}
              />
            </div>
            <Champ
              libelle="Taux de TVA (%)"
              cle="vat_rate"
              valeur={config.tvaTaux}
              largeur={80}
              infobulle="Taux normal au Mali : 18 %. Saisi en pourcentage, stocké en fraction. Sans assujettissement, ce taux n'est pas appliqué."
            />
            <p style={{ fontSize: 11.5, color: 'var(--n400)', margin: '10px 0 0', lineHeight: 1.5 }}>
              La taxe est <b>extraite</b> des montants facturés, qui sont donc traités comme TTC.
              C'est ce que le client a réellement payé : l'ajouter par-dessus ferait entrer en
              caisse plus d'argent qu'il n'en est passé.
            </p>
          </div>
        </div>
      </div>

      <CartePaysDefaut surErreur={setErreur} />

      {/* Frais des opérateurs — migration 0044. La comptabilité les calcule
          sur chaque encaissement et chaque envoi ; vide = inconnu, signalé. */}
      <div className="card">
        <div className="card-header">Frais mobile money</div>
        <div className="card-body">
          {config.fraisMM.encaissementWave === undefined ? (
            <p className="avis avis-info" style={{ marginTop: 0 }}>
              Ces réglages apparaîtront une fois la migration 0044 appliquée en production.
            </p>
          ) : (
            <>
              <Champ libelle="Wave — sur un paiement client (%)" cle="frais_mm_encaissement_wave" valeur={config.fraisMM.encaissementWave ?? ''} largeur={80} infobulle="Pourcentage prélevé par Wave sur chaque paiement reçu. Laisser vide tant que le contrat n'est pas signé." />
              <Champ libelle="Wave — sur un envoi (%)" cle="frais_mm_decaissement_wave" valeur={config.fraisMM.decaissementWave ?? ''} largeur={80} infobulle="Paie des livreurs, reversements aux marchands, remboursements." />
              <Champ libelle="Orange Money — sur un paiement client (%)" cle="frais_mm_encaissement_om" valeur={config.fraisMM.encaissementOm ?? ''} largeur={80} />
              <Champ libelle="Orange Money — sur un envoi (%)" cle="frais_mm_decaissement_om" valeur={config.fraisMM.decaissementOm ?? ''} largeur={80} />
              <p style={{ fontSize: 11.5, color: 'var(--n400)', margin: '10px 0 0', lineHeight: 1.5 }}>
                Un champ vide veut dire « taux pas encore connu » : la comptabilité ne compte alors aucun
                frais et l’affiche en alerte, plutôt que de supposer zéro. Les frais hors taux (tenue de
                compte, régularisation d’après relevé) restent en saisie manuelle.
              </p>
            </>
          )}
        </div>
      </div>

      <CartePaliers
        paliers={config.paliersLivraison}
        minima={config.minimaLivraison}
        fraisService={{ bas: config.fraisService, haut: config.fraisServiceHaut }}
        reductionPremium={config.reductionPremium}
        surEnregistre={charger}
        surErreur={setErreur}
      />

      {/* Majoration de forte demande — migration 0054. */}
      <div className="card">
        <div className="card-header">Forte demande (majoration de tension)</div>
        <div className="card-body">
          {config.tension === null || migrationAbsente ? (
            <p className="avis avis-info" style={{ marginTop: 0 }}>
              Ces réglages apparaîtront une fois la migration 0054 appliquée en production.
            </p>
          ) : (
            <>
              <div className="config-field">
                <span className="cf-label">Majoration activée</span>
                <ListeDeroulante
                  valeur={config.tension.active ? 'oui' : 'non'}
                  options={[
                    { valeur: 'non', libelle: 'Non' },
                    { valeur: 'oui', libelle: 'Oui' },
                  ]}
                  surChangement={(v) => {
                    void enregistrerReglage('tension_active', v)
                      .then(charger)
                      .catch((err: unknown) =>
                        setErreur(`Enregistrement refusé : ${err instanceof Error ? err.message : String(err)}`),
                      )
                  }}
                />
              </div>
              <Champ libelle="Rayon autour du restaurant (km)" cle="tension_rayon_km" valeur={config.tension.rayonKm} largeur={80} />
              <Champ
                libelle="Seuil (commandes par livreur)"
                cle="tension_seuil"
                valeur={config.tension.seuil}
                largeur={80}
                infobulle="Au-dessus de ce ratio (commandes en attente ÷ livreurs disponibles), la majoration commence."
              />
              <Champ libelle="Pente (% par point de ratio)" cle="tension_pente_pct" valeur={config.tension.pentePct} largeur={80} />
              <Champ libelle="Plafond (%)" cle="tension_plafond_pct" valeur={config.tension.plafondPct} largeur={80} />
              <Champ
                libelle="Part reversée au livreur (%)"
                cle="tension_part_livreur_pct"
                valeur={config.tension.partLivreurPct}
                largeur={80}
              />
              <p className="aide">
                Autour du restaurant : <b>ratio</b> = commandes payées en attente de livreur ÷ livreurs
                disponibles dont la position a moins de 5 min (au moins 1). Majoration = (ratio − seuil) ×
                pente, entre 0 et le plafond, appliquée aux frais de livraison du client — même si la livraison
                est offerte. Le client la voit sur une ligne « Forte demande » avant de payer ; le livreur en
                reçoit la part indiquée en plus de sa paie. Exemple avec ces réglages : 3 commandes pour 1
                livreur = ratio 3, majoration de{' '}
                {Math.min(config.tension.plafondPct, Math.max(0, (3 - config.tension.seuil) * config.tension.pentePct))} %.
              </p>
              <p className="avis avis-warn">
                L'app livreur n'envoie sa position que pendant une course : un livreur disponible n'est compté
                que dans les 5 minutes qui suivent sa dernière livraison. Tant que ce point n'est pas tranché, la
                majoration peut monter dès 2 commandes en attente dans la zone.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Traversée du Niger — migration 0043. */}
      <div className="card">
        <div className="card-header">Traversée du fleuve</div>
        <div className="card-body">
          {(config.fraisTraversee === null || config.bonusTraversee === null || migrationAbsente) && (
            <p className="avis avis-info" style={{ marginBottom: 10 }}>
              {MESSAGE_MIGRATION_0043}
            </p>
          )}
          <Champ
            libelle="Supplément traversée (client, FCFA)"
            cle="frais_traversee"
            valeur={config.fraisTraversee ?? 0}
            desactive={config.fraisTraversee === null || migrationAbsente}
            infobulle="Ajouté aux frais de livraison du client. Offert avec la livraison quand le panier dépasse le seuil de livraison gratuite."
          />
          <Champ
            libelle="Bonus traversée (livreur, FCFA)"
            cle="bonus_traversee"
            valeur={config.bonusTraversee ?? 0}
            desactive={config.bonusTraversee === null || migrationAbsente}
            infobulle="Ajouté à la part du livreur. Reste dû même quand la livraison est offerte au client."
          />
          <p className="aide">
            S'applique quand le restaurant et le client sont sur deux rives du Niger : la distance est
            mesurée à vol d'oiseau, et il faut passer par un pont. Le supplément s'ajoute aux frais de
            livraison du client ; le bonus s'ajoute à ce que touche le livreur.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Barème météo</div>
        <div className="card-body">
          <table>
            <thead>
              <tr>
                <th>Condition</th>
                <th>Bonus livreur</th>
                <th>Supplément client</th>
              </tr>
            </thead>
            <tbody>
              {config.bonusMeteo.map((b) => (
                <tr key={b.id}>
                  <td>{libelleMeteo(b.condition)}</td>
                  <td>
                    <ChampNombre
                      valeur={b.bonusLivreur}
                      surEnregistrer={(v) => enregistrerBonusMeteo(b.id, v, b.supplementClient)}
                      surErreur={setErreur}
                      surSucces={charger}
                    />{' '}
                    FCFA
                  </td>
                  <td>
                    <ChampNombre
                      valeur={b.supplementClient}
                      surEnregistrer={(v) => enregistrerBonusMeteo(b.id, b.bonusLivreur, v)}
                      surErreur={setErreur}
                      surSucces={charger}
                    />{' '}
                    FCFA
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="aide">
            Montants appliqués quand la météo en cours (carte « Météo en cours », en haut) n'est pas
            « Normale ».
          </p>
        </div>
      </div>
    </>
  )
}

// ── Pays par défaut des numéros ──────────────────────────────────────────

/**
 * Pays proposé d'office dans tous les champs « numéro WhatsApp » (apps et consoles) : `platform_config.pays_defaut`
 * (migration 0051). Une personne qui a déjà choisi un autre pays sur son appareil garde son choix.
 */
function CartePaysDefaut({ surErreur }: { surErreur: (m: string | null) => void }) {
  /* undefined = lecture en cours ; null = migration 0051 pas encore appliquée. */
  const [pays, setPays] = useState<string | null | undefined>(undefined)
  const [enCours, setEnCours] = useState(false)

  useEffect(() => {
    let vivant = true
    void lirePaysDefautConfig()
      .then((p) => vivant && setPays(p))
      .catch(() => vivant && setPays(null))
    return () => {
      vivant = false
    }
  }, [])

  async function choisir(iso: string) {
    setEnCours(true)
    try {
      await enregistrerPaysDefaut(iso)
      setPays(iso)
      surErreur(null)
    } catch (err) {
      surErreur(`Enregistrement refusé : ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div className="card">
      <div className="card-header">Connexion par numéro WhatsApp</div>
      <div className="card-body">
        {pays === null ? (
          <p className="avis avis-info" style={{ marginTop: 0 }}>
            Ce réglage apparaîtra une fois la migration 0051 appliquée en production.
          </p>
        ) : (
          <div className="config-field">
            <span className="cf-label">Pays par défaut des numéros</span>
            <ListeDeroulante
              valeur={pays ?? ''}
              options={optionsPays()}
              desactive={pays === undefined || enCours}
              surChangement={(v) => void choisir(v)}
            />
          </div>
        )}
        <p className="aide">
          Pays proposé d'office dans les champs « numéro WhatsApp » (connexion, création de comptes) des
          apps et des consoles. Chaque personne peut en choisir un autre : son choix est mémorisé sur son
          appareil.
        </p>
      </div>
    </div>
  )
}

function ChampNombre({
  valeur,
  surEnregistrer,
  surErreur,
  surSucces,
}: {
  valeur: number
  surEnregistrer: (v: number) => Promise<void>
  surErreur: (m: string) => void
  surSucces: () => Promise<void>
}) {
  return (
    <input
      type="number"
      className="champ-n"
      defaultValue={valeur}
      onBlur={(e) => {
        const v = Number(e.target.value)
        if (v === valeur) return
        void surEnregistrer(v)
          .then(surSucces)
          .catch((err: unknown) => {
            surErreur(`Enregistrement refusé : ${err instanceof Error ? err.message : String(err)}`)
            e.target.value = String(valeur)
          })
      }}
    />
  )
}

// ── Météo en cours ───────────────────────────────────────────────────────
// La carte (mode automatique / manuel, Arrêter / Forcer, règles, journal) vit dans
// `ui/meteo/CarteMeteo.tsx` depuis la migration 0061 (météo automatique).

// ── Paliers de livraison ─────────────────────────────────────────────────

/** Une ligne en cours d'édition : tout en TEXTE, pour qu'une saisie à moitié tapée reste telle quelle. */
interface LigneBrouillon {
  cle: string
  id: string | null
  minKm: string
  maxKm: string
  client: string
  livreur: string
  /** `premium_fee` : inutilisé depuis 0055, recopié tel quel à l'enregistrement. */
  premium: string
}

/** Les réglages de `platform_config` saisis dans la même carte (0054), en texte eux aussi. */
interface ReglagesBrouillon {
  minClient: string
  minLivreur: string
  serviceBas: string
  /** Vide = frais de service fixes. */
  serviceHaut: string
}

let compteurLignes = 0
const nouvelleCle = () => `n${++compteurLignes}`

const versBrouillon = (p: PalierLivraison): LigneBrouillon => ({
  cle: `p${p.id}`,
  id: p.id,
  minKm: fmtKm(p.minKm),
  maxKm: fmtKm(p.maxKm),
  client: String(p.fraisClient),
  livreur: String(p.partLivreur),
  premium: String(p.fraisPremium),
})

const versSaisie = (l: LigneBrouillon): PalierASaisir & { id: string | null } => ({
  id: l.id,
  minKm: lireNombre(l.minKm),
  maxKm: lireNombre(l.maxKm),
  fraisClient: lireNombre(l.client),
  partLivreur: lireNombre(l.livreur),
  fraisPremium: lireNombre(l.premium),
})

const reglagesVersBrouillon = (
  m: { client: number; premium: number; driver: number } | null,
  f: FraisServiceASaisir,
): ReglagesBrouillon => ({
  minClient: m ? String(m.client) : '',
  minLivreur: m ? String(m.driver) : '',
  serviceBas: String(f.bas),
  serviceHaut: f.haut === null ? '' : String(f.haut),
})

const lireMinima = (r: ReglagesBrouillon): MinimaASaisir => ({
  client: lireNombre(r.minClient),
  driver: lireNombre(r.minLivreur),
})

const lireFraisService = (r: ReglagesBrouillon): FraisServiceASaisir => ({
  bas: lireNombre(r.serviceBas),
  haut: r.serviceHaut.trim() === '' ? null : lireNombre(r.serviceHaut),
})

/** Contenu comparable (sans la clé React) : sert à savoir s'il y a du non enregistré. */
const empreinte = (ls: LigneBrouillon[]) =>
  JSON.stringify(ls.map(({ id, minKm, maxKm, client, livreur, premium }) => [id, minKm, maxKm, client, livreur, premium]))

/**
 * ⚠️ `Prix client` et `Paie livreur` sont DEUX colonnes distinctes, et l'écart
 * entre elles EST notre marge sur la livraison. Les confondre fausserait à la
 * fois la paie du livreur et la comptabilité.
 *
 * Prix PROGRESSIF (0054) : chaque palier donne le prix à sa distance maxi, la
 * ligne « 0 km » le prix minimum ; le serveur interpole entre les deux. L'aperçu
 * sous le tableau est calculé par le MÊME module que le serveur
 * (`_shared/pricing.ts`), sur le brouillon, avant tout enregistrement.
 *
 * Le tableau est édité en BROUILLON et n'écrit en base qu'au clic sur
 * « Enregistrer » : un barème se tient d'un bloc (un palier modifié seul
 * ouvrirait un trou ou un chevauchement le temps de modifier le voisin), et
 * chaque commande passée entre-temps serait facturée avec le barème bancal.
 */
function CartePaliers({
  paliers,
  minima,
  fraisService,
  reductionPremium,
  surEnregistre,
  surErreur,
}: {
  paliers: PalierLivraison[]
  /** `null` = base sans la migration 0054 : minima et frais de service progressifs non réglables. */
  minima: { client: number; premium: number; driver: number } | null
  fraisService: FraisServiceASaisir
  /** Réduction Premium en % (0055) — pour l'aperçu ; `null` = base sans 0055. */
  reductionPremium: number | null
  surEnregistre: () => Promise<void>
  surErreur: (m: string | null) => void
}) {
  const sans0054 = minima === null
  const [lignes, setLignes] = useState<LigneBrouillon[]>(() => paliers.map(versBrouillon))
  const [reglages, setReglages] = useState<ReglagesBrouillon>(() => reglagesVersBrouillon(minima, fraisService))
  const [aSupprimer, setASupprimer] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const [confirme, setConfirme] = useState(false)

  /* Le barème du serveur a changé (après notre enregistrement, ou ailleurs) :
     on repart de lui. Un rechargement dû à un AUTRE champ de l'écran ne change
     pas cette empreinte — le brouillon en cours n'est donc pas écrasé. */
  const empreinteServeur = empreinte(paliers.map(versBrouillon))
  const reglagesServeur = reglagesVersBrouillon(minima, fraisService)
  const empreinteReglagesServeur = JSON.stringify(reglagesServeur)
  useEffect(() => {
    setLignes(paliers.map(versBrouillon))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empreinteServeur])
  useEffect(() => {
    setReglages(reglagesServeur)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empreinteReglagesServeur])

  const minimaModifies =
    !sans0054 &&
    (reglages.minClient !== reglagesServeur.minClient ||
      reglages.minLivreur !== reglagesServeur.minLivreur)
  const serviceModifie =
    !sans0054 &&
    (reglages.serviceBas !== reglagesServeur.serviceBas || reglages.serviceHaut !== reglagesServeur.serviceHaut)
  const modifie = empreinte(lignes) !== empreinteServeur || minimaModifies || serviceModifie
  const saisies = lignes.map(versSaisie)
  const minimaSaisis = sans0054 ? null : lireMinima(reglages)
  const serviceSaisi = sans0054 ? fraisService : lireFraisService(reglages)
  const validation = validerPaliers(saisies, minimaSaisis ?? undefined)
  const erreurs = [
    ...validation.erreurs,
    ...(minimaSaisis ? validerMinima(minimaSaisis) : []),
    ...(sans0054 ? [] : validerFraisService(serviceSaisi)),
  ]
  const { avertissements } = validation
  const lignesEnErreur = new Set(erreurs.map((e) => e.ligne))
  const lignesAvert = new Set(avertissements.map((e) => e.ligne))
  const apercu = apercuPrix(saisies, minimaSaisis ?? { client: 0, driver: 0 }, serviceSaisi, reductionPremium ?? 0)
  const distanceMaxi = Math.max(0, ...saisies.map((p) => (Number.isFinite(p.maxKm) ? p.maxKm : 0)))

  function maj(cle: string, champ: keyof Omit<LigneBrouillon, 'cle' | 'id'>, valeur: string) {
    setConfirme(false)
    setLignes((ls) => ls.map((l) => (l.cle === cle ? { ...l, [champ]: valeur } : l)))
  }

  function majReglage(champ: keyof ReglagesBrouillon, valeur: string) {
    setConfirme(false)
    setReglages((r) => ({ ...r, [champ]: valeur }))
  }

  function ajouter() {
    setConfirme(false)
    setLignes((ls) => {
      const dernier = ls[ls.length - 1]
      const fin = dernier ? lireNombre(dernier.maxKm) : 0
      const debut = Number.isFinite(fin) ? fin : 0
      return [
        ...ls,
        {
          cle: nouvelleCle(),
          id: null,
          minKm: fmtKm(debut),
          maxKm: fmtKm(Math.min(debut + 5, 999.9)),
          client: dernier?.client ?? '0',
          livreur: dernier?.livreur ?? '0',
          premium: dernier?.premium ?? '0',
        },
      ]
    })
  }

  function demanderSuppression(l: LigneBrouillon) {
    // Un palier jamais enregistré ne coûte rien à retirer : pas de confirmation.
    if (l.id === null) setLignes((ls) => ls.filter((x) => x.cle !== l.cle))
    else setASupprimer(l.cle)
  }

  const aRetirer = lignes.find((l) => l.cle === aSupprimer) ?? null

  async function enregistrer() {
    if (erreurs.length || !modifie) return
    setEnCours(true)
    try {
      const ids = new Set(lignes.map((l) => l.id).filter((i): i is string => i !== null))
      const idsASupprimer = paliers.map((p) => p.id).filter((i) => !ids.has(i))
      await enregistrerPaliersLivraison(saisies, idsASupprimer, {
        ...(minimaModifies && minimaSaisis ? { minima: minimaSaisis } : {}),
        ...(serviceModifie ? { fraisService: serviceSaisi } : {}),
      })
      surErreur(null)
      setConfirme(true)
      await surEnregistre()
    } catch (err) {
      surErreur(`Enregistrement refusé : ${err instanceof Error ? err.message : String(err)}`)
      // Barème possiblement à moitié écrit : on montre celui de la base.
      await surEnregistre().catch(() => undefined)
    } finally {
      setEnCours(false)
    }
  }

  const classe = (i: number) => `champ-n${lignesEnErreur.has(i) ? ' err' : lignesAvert.has(i) ? ' warn' : ''}`
  const classeGlobale = `champ-n${lignesEnErreur.has(null) ? ' err' : ''}`

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">Tarifs livraison par distance</div>
      <div className="card-body">
        <table>
          <thead>
            <tr>
              <th>De (km)</th>
              <th>À (km)</th>
              <th>Prix client à cette distance</th>
              <th>Paie livreur à cette distance</th>
              <th>Marge</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={2} style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                0 km — prix minimum
              </td>
              {sans0054 ? (
                <td colSpan={4} style={{ color: 'var(--n400)', fontSize: 12.5 }}>
                  Réglable après la mise à jour de la base (migration 0054).
                </td>
              ) : (
                <>
                  <td>
                    <input className={classeGlobale} inputMode="numeric" aria-label="Prix client minimum, à 0 km" value={reglages.minClient} onChange={(e) => majReglage('minClient', e.target.value)} />
                  </td>
                  <td>
                    <input className={classeGlobale} inputMode="numeric" aria-label="Paie livreur minimum, à 0 km" value={reglages.minLivreur} onChange={(e) => majReglage('minLivreur', e.target.value)} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--n400)' }}>
                    {minimaSaisis && Number.isFinite(minimaSaisis.client - minimaSaisis.driver)
                      ? `${minimaSaisis.client - minimaSaisis.driver > 0 ? '+' : ''}${fcfa(minimaSaisis.client - minimaSaisis.driver)}`
                      : '—'}
                  </td>
                  <td />
                </>
              )}
            </tr>
            {lignes.map((l, i) => {
              const s = saisies[i]!
              const marge = s.fraisClient - s.partLivreur
              const a = Number.isFinite(s.maxKm) ? `${fmtKm(s.maxKm)} km` : 'la distance maxi'
              return (
                <tr key={l.cle}>
                  <td>
                    <input className={classe(i)} inputMode="decimal" aria-label={`Palier ${i + 1}, distance mini`} value={l.minKm} onChange={(e) => maj(l.cle, 'minKm', e.target.value)} />
                  </td>
                  <td>
                    <input className={classe(i)} inputMode="decimal" aria-label={`Palier ${i + 1}, distance maxi`} value={l.maxKm} onChange={(e) => maj(l.cle, 'maxKm', e.target.value)} />
                  </td>
                  <td>
                    <input className={classe(i)} inputMode="numeric" aria-label={`Prix client à ${a}`} title={`Prix client à ${a}`} value={l.client} onChange={(e) => maj(l.cle, 'client', e.target.value)} />
                  </td>
                  <td>
                    <input className={classe(i)} inputMode="numeric" aria-label={`Paie livreur à ${a}`} title={`Paie livreur à ${a}`} value={l.livreur} onChange={(e) => maj(l.cle, 'livreur', e.target.value)} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontWeight: 600, color: Number.isFinite(marge) && marge < 0 ? 'var(--danger)' : 'var(--n400)' }}>
                    {Number.isFinite(marge) ? `${marge > 0 ? '+' : ''}${fcfa(marge)}` : '—'}
                  </td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => demanderSuppression(l)}>
                      Supprimer
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {!sans0054 && (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
            <label style={{ fontSize: 13, color: 'var(--n700)', display: 'flex', alignItems: 'center', gap: 8 }}>
              Frais de service à 0 km
              <input className={classeGlobale} inputMode="numeric" aria-label="Frais de service à 0 km" value={reglages.serviceBas} onChange={(e) => majReglage('serviceBas', e.target.value)} />
            </label>
            <label style={{ fontSize: 13, color: 'var(--n700)', display: 'flex', alignItems: 'center', gap: 8 }}>
              Frais de service à {fmtKm(distanceMaxi)} km (distance maxi)
              <input className={classeGlobale} inputMode="numeric" placeholder="fixes" aria-label="Frais de service à la distance maxi" value={reglages.serviceHaut} onChange={(e) => majReglage('serviceHaut', e.target.value)} />
            </label>
          </div>
        )}

        <p className="aide">
          Le prix est <b>progressif</b> : chaque palier donne le prix à sa distance « À » ; entre deux
          distances, le prix monte en ligne droite (arrondi aux 50 F), en partant du prix minimum à 0 km et
          sans jamais descendre sous ce minimum. Au-delà du dernier palier, on applique le dernier prix. Un
          prix ne peut pas baisser quand la distance augmente. Un abonné Premium paie le prix client moins la
          réduction Premium (carte « Tarification plateforme »), arrondi aux 50 F. Les
          frais de service montent de la même façon entre 0 km et la distance maxi (arrondi aux 25 F) ;
          laissez la limite haute vide pour des frais fixes. Ils restent dus quand la livraison est offerte.
        </p>

        <ApercuPrix lignes={apercu} />

        {erreurs.length > 0 && (
          <div className="avis avis-err" role="alert">
            <b>À corriger avant d'enregistrer</b>
            <ul>
              {erreurs.map((e, k) => (
                <li key={k}>{e.message}</li>
              ))}
            </ul>
          </div>
        )}
        {avertissements.length > 0 && (
          <div className="avis avis-warn">
            <b>À vérifier (n'empêche pas d'enregistrer)</b>
            <ul>
              {avertissements.map((e, k) => (
                <li key={k}>{e.message}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="actions-paliers">
          <button className="btn btn-outline btn-sm" onClick={ajouter}>
            Ajouter un palier
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => {
              const tries = trierPaliers(lignes.map((l) => ({ l, minKm: lireNombre(l.minKm), maxKm: lireNombre(l.maxKm) })))
              setLignes(tries.map((t) => t.l))
            }}
          >
            Trier par distance
          </button>
          <button
            className="btn btn-outline btn-sm"
            disabled={!modifie}
            onClick={() => {
              setLignes(paliers.map(versBrouillon))
              setReglages(reglagesServeur)
              setConfirme(false)
            }}
          >
            Annuler les modifications
          </button>
          <span style={{ flex: 1 }} />
          {confirme && !modifie && <span className="ok-enreg">Barème enregistré</span>}
          {modifie && <span className="non-enreg">Modifications non enregistrées</span>}
          <button
            className="btn btn-accent btn-sm"
            disabled={!modifie || erreurs.length > 0 || enCours}
            onClick={() => void enregistrer()}
          >
            {enCours ? 'Enregistrement…' : 'Enregistrer les tarifs'}
          </button>
        </div>
      </div>

      <Modale titre="Supprimer ce palier ?" ouverte={aRetirer !== null} surFermer={() => setASupprimer(null)}>
        {aRetirer && (
          <>
            <p style={{ fontSize: 13.5, lineHeight: 1.55 }}>
              Retirer le palier de <b>{aRetirer.minKm}</b> à <b>{aRetirer.maxKm} km</b> (client{' '}
              {aRetirer.client} FCFA, livreur {aRetirer.livreur} FCFA) ? Les distances qu'il couvrait
              devront être reprises par un autre palier, sans quoi l'enregistrement sera refusé.
              Rien n'est modifié en base avant « Enregistrer les tarifs ».
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setASupprimer(null)}>
                Garder
              </button>
              <button
                className="btn btn-danger-sm btn-sm"
                onClick={() => {
                  setLignes((ls) => ls.filter((x) => x.cle !== aRetirer.cle))
                  setASupprimer(null)
                  setConfirme(false)
                }}
              >
                Supprimer le palier
              </button>
            </div>
          </>
        )}
      </Modale>
    </div>
  )
}

// ── Aperçu des prix (brouillon) ──────────────────────────────────────────

/**
 * Ce que paieraient le client et l'abonné, et ce que toucherait le livreur, aux
 * distances de référence — calculé sur le BROUILLON, avant enregistrement. Une
 * petite courbe en dessous montre la forme : une marche ou une pente qui
 * redescend s'y voit tout de suite.
 */
function ApercuPrix({ lignes }: { lignes: LigneApercu[] }) {
  const L = 560
  const H = 150
  const marge = { g: 44, d: 22, h: 8, b: 22 }
  const kmMax = Math.max(...DISTANCES_APERCU)
  const prixMax = Math.max(1, ...lignes.flatMap((l) => [l.client, l.premium, l.driver]))
  const x = (km: number) => marge.g + (km / kmMax) * (L - marge.g - marge.d)
  const y = (v: number) => H - marge.b - (v / prixMax) * (H - marge.h - marge.b)
  const trace = (champ: 'client' | 'premium' | 'driver') =>
    lignes.map((l) => `${x(l.km).toFixed(1)},${y(l[champ]).toFixed(1)}`).join(' ')
  const series = [
    { champ: 'client' as const, libelle: 'Prix client', couleur: 'var(--or)' },
    { champ: 'driver' as const, libelle: 'Paie livreur', couleur: 'var(--vert)' },
    { champ: 'premium' as const, libelle: 'Prix Premium', couleur: 'var(--n400)' },
  ]
  return (
    <div style={{ marginTop: 14 }}>
      <b style={{ fontSize: 13 }}>Aperçu avant enregistrement</b>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ marginTop: 6 }}>
          <thead>
            <tr>
              <th>Distance</th>
              {lignes.map((l) => (
                <th key={l.km} style={{ textAlign: 'right' }}>{fmtKm(l.km)} km</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['Prix client', (l: LigneApercu) => l.client],
              ['Prix Premium (−réduction)', (l: LigneApercu) => l.premium],
              ['Paie livreur', (l: LigneApercu) => l.driver],
              ['Marge', (l: LigneApercu) => l.client - l.driver],
              ['Frais de service', (l: LigneApercu) => l.service],
            ].map(([libelle, valeur]) => (
              <tr key={libelle as string}>
                <td style={{ whiteSpace: 'nowrap' }}>{libelle as string}</td>
                {lignes.map((l) => (
                  <td key={l.km} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {(valeur as (l: LigneApercu) => number)(l).toLocaleString('fr-FR')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <svg viewBox={`0 0 ${L} ${H}`} role="img" aria-label="Courbe des prix de livraison selon la distance" style={{ width: '100%', maxWidth: L, marginTop: 8 }}>
        <line x1={marge.g} y1={H - marge.b} x2={L - marge.d} y2={H - marge.b} stroke="var(--bord)" />
        <line x1={marge.g} y1={marge.h} x2={marge.g} y2={H - marge.b} stroke="var(--bord)" />
        {[0, prixMax / 2, prixMax].map((v) => (
          <text key={v} x={marge.g - 6} y={y(v) + 4} fontSize="10" textAnchor="end" fill="var(--n400)">
            {Math.round(v).toLocaleString('fr-FR')}
          </text>
        ))}
        {[0, 3, 8, 12, 18].map((km) => (
          <text key={km} x={x(km)} y={H - 6} fontSize="10" textAnchor="middle" fill="var(--n400)">
            {km} km
          </text>
        ))}
        {series.map((s) => (
          <polyline key={s.champ} points={trace(s.champ)} fill="none" stroke={s.couleur} strokeWidth="2" />
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--n700)', flexWrap: 'wrap' }}>
        {series.map((s) => (
          <span key={s.champ} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 14, height: 3, background: s.couleur, display: 'inline-block', borderRadius: 2 }} />
            {s.libelle}
          </span>
        ))}
      </div>
    </div>
  )
}
